/**
 * ControlPanel component with Context
 * Updated with new phase layout and improved UI
 */

import React, { useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu';
import {
  FilterIcon,
  SearchIcon,
  BarChartHorizontalIcon,
  NetworkIcon,
  LayersIcon,
  ZoomInIcon,
  ZoomOutIcon,
  RefreshCwIcon,
  EyeIcon,
  Clock,
  ListIcon,
  PlayIcon,
  PauseIcon,
  EyeOffIcon
} from 'lucide-react';
import { useMapLayout, useMapUI, useMapState } from '@/contexts/map';
import { useReactFlow } from '@xyflow/react';
import { FilterVisibility } from '@/contexts/map/MapUIContext';

/**
 * ControlPanel component with Context
 */
const ControlPanel: React.FC = () => {
  // Get state and operations from contexts
  const { layoutType, changeLayout } = useMapLayout();
  const { 
    searchTerm, 
    setSearchTerm, 
    selectedPhases, 
    setSelectedPhases, 
    minImportance, 
    setMinImportance,
    filterVisibility,
    setFilterVisibility,
    animateLayout,
    toggleAnimateLayout
  } = useMapUI();
  const { mapData } = useMapState();
  
  // Get ReactFlow instance for zoom controls
  const { zoomIn, zoomOut, fitView } = useReactFlow();
  
  // Handle search
  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, [setSearchTerm]);
  
  // Handle phase filter change
  const handlePhaseFilterChange = useCallback((phaseId: string, checked: boolean) => {
    const newSelectedPhases = checked
      ? [...selectedPhases, phaseId]
      : selectedPhases.filter(id => id !== phaseId);
    
    setSelectedPhases(newSelectedPhases);
  }, [selectedPhases, setSelectedPhases]);
  
  // Handle importance filter change
  const handleImportanceFilterChange = useCallback((level: number, checked: boolean) => {
    if (checked) {
      setMinImportance(level);
    }
  }, [setMinImportance]);
  
  // Handle filter visibility change
  const handleFilterVisibilityChange = useCallback((value: string) => {
    setFilterVisibility(value as FilterVisibility);
  }, [setFilterVisibility]);
  
  // Handle layout change
  const handleLayoutChange = useCallback((layout: 'timeline' | 'relation' | 'phase' | 'content-timeline') => {
    console.log('Changing layout to:', layout);
    changeLayout(layout);
  }, [changeLayout]);
  
  // Handle zoom in
  const handleZoomIn = useCallback(() => {
    zoomIn();
  }, [zoomIn]);
  
  // Handle zoom out
  const handleZoomOut = useCallback(() => {
    zoomOut();
  }, [zoomOut]);
  
  // Handle fit view
  const handleFitView = useCallback(() => {
    fitView({ padding: 0.2, duration: 800 });
  }, [fitView]);
  
  // Calculate number of active filters
  const filterCount = React.useMemo(() => {
    let count = 0;
    if (selectedPhases.length > 0) count++;
    if (minImportance > 1) count++;
    if (filterVisibility !== 'all') count++;
    return count;
  }, [selectedPhases, minImportance, filterVisibility]);
  
  // If no mapData yet, show loading state
  if (!mapData || !mapData.phases) {
    return <div>Loading...</div>;
  }
  
  return (
    <div className="flex items-center space-x-3">
      {/* Layout buttons group */}
      <div className="flex space-x-1 bg-muted rounded-md p-1">
        {/* タイムラインレイアウトを非表示に */}
        {/* <Button
          variant={layoutType === 'timeline' ? "default" : "ghost"}
          size="icon"
          onClick={() => handleLayoutChange('timeline')}
          title="タイムラインレイアウト"
          className="h-8 w-8"
        >
          <BarChartHorizontalIcon className="h-4 w-4" />
        </Button> */}
        
        {/* 関連性レイアウトを非表示に */}
        {/* <Button
          variant={layoutType === 'relation' ? "default" : "ghost"}
          size="icon"
          onClick={() => handleLayoutChange('relation')}
          title="関連性レイアウト"
          className="h-8 w-8"
        >
          <NetworkIcon className="h-4 w-4" />
        </Button> */}
        
        {/* フェーズ別レイアウト（デフォルト・唯一表示） */}
        <Button
          variant={layoutType === 'phase' ? "default" : "ghost"}
          size="icon"
          onClick={() => handleLayoutChange('phase')}
          title="フェーズ別レイアウト"
          className="h-8 w-8"
        >
          <LayersIcon className="h-4 w-4" />
        </Button>
        
        {/* コンテンツタイムラインを非表示に */}
        {/* <Button
          variant={layoutType === 'content-timeline' ? "default" : "ghost"}
          size="icon"
          onClick={() => handleLayoutChange('content-timeline')}
          title="コンテンツタイムライン"
          className="h-8 w-8"
        >
          <Clock className="h-4 w-4" />
        </Button> */}
      </div>
      
      {/* Zoom controls */}
      <div className="flex space-x-1 bg-muted rounded-md p-1">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={handleZoomIn}
          title="拡大"
          className="h-8 w-8"
        >
          <ZoomInIcon className="h-4 w-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={handleZoomOut}
          title="縮小"
          className="h-8 w-8"
        >
          <ZoomOutIcon className="h-4 w-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={handleFitView}
          title="全体を表示"
          className="h-8 w-8"
        >
          <RefreshCwIcon className="h-4 w-4" />
        </Button>
      </div>
      
      {/* Search input */}
      <div className="relative flex-1 max-w-[200px]">
        <SearchIcon className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="検索..."
          className="pl-8 h-9"
          value={searchTerm}
          onChange={handleSearch}
        />
      </div>
      
      {/* Display options dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant={filterVisibility !== 'all' ? "default" : "outline"} 
            size="sm" 
            className="h-9"
          >
            <EyeIcon className="h-4 w-4 mr-2" />
            表示
            {filterVisibility !== 'all' && (
              <span className="ml-1 bg-primary-foreground text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs">
                !
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuLabel className="text-xs">表示フィルター</DropdownMenuLabel>
          <DropdownMenuRadioGroup 
            value={filterVisibility} 
            onValueChange={handleFilterVisibilityChange}
          >
            <DropdownMenuRadioItem value="all" className="text-xs">
              <EyeIcon className="h-3.5 w-3.5 mr-2" />
              すべて表示
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="active-only" className="text-xs">
              <EyeIcon className="h-3.5 w-3.5 mr-2" />
              アクティブのみ
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="inactive-only" className="text-xs">
              <EyeOffIcon className="h-3.5 w-3.5 mr-2" />
              非アクティブのみ
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      
      {/* Filter dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant={filterCount > 0 ? "default" : "outline"} 
            size="sm" 
            className="h-9"
          >
            <FilterIcon className="h-4 w-4 mr-2" />
            フィルター
            {filterCount > 0 && (
              <span className="ml-1 bg-primary-foreground text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs">
                {filterCount}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuLabel className="text-xs">フェーズ</DropdownMenuLabel>
          {mapData.phases.map(phase => (
            <DropdownMenuCheckboxItem
              key={phase.id}
              checked={selectedPhases.length === 0 || selectedPhases.includes(phase.id)}
              onCheckedChange={(checked: boolean) => handlePhaseFilterChange(phase.id, checked)}
              className="text-xs"
            >
              <span 
                className="w-2 h-2 rounded-full mr-2" 
                style={{ background: phase.color }}
              />
              {phase.name}
            </DropdownMenuCheckboxItem>
          ))}
          
          <DropdownMenuSeparator />
          
          <DropdownMenuLabel className="text-xs">重要度</DropdownMenuLabel>
          {[1, 2, 3, 4, 5].map(level => (
            <DropdownMenuCheckboxItem
              key={level}
              checked={level >= minImportance}
              onCheckedChange={(checked: boolean) => handleImportanceFilterChange(level, checked)}
              className="text-xs"
            >
              <div className="flex items-center mr-2">
                {Array.from({ length: level }).map((_, i) => (
                  <div key={i} className="w-1 h-1 rounded-full bg-foreground mr-0.5" />
                ))}
              </div>
              レベル {level} 以上
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default ControlPanel;